import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Grid, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MicrosoftIcon from '@mui/icons-material/Window';
import type { MsStatus } from '../../hooks/useMicrosoftIntegration';

interface Props {
  msStatus: MsStatus;
  msDisconnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

/**
 * True when the connection works but lacks the file scope Prospo now asks for —
 * the state every existing user lands in whenever that scope changes, since a
 * refresh token cannot acquire a scope it was not originally consented to.
 *
 * Checks that the list is non-empty rather than naming a scope: the server
 * populates missingScopes purely from the OneDrive requirement, so anything in
 * it means file access is missing. Matching on a literal meant this silently
 * stopped firing when the requirement moved from Files.Read to Files.Read.All.
 *
 * Only asserted for a live connection: while disconnected the card is already
 * about connecting, and greying one chip there is noise.
 */
const isFileAccessMissing = (msStatus: MsStatus): boolean =>
  msStatus.isConnected && !msStatus.loading && msStatus.missingScopes.length > 0;

const MicrosoftIntegrationSection = ({ msStatus, msDisconnecting, onConnect, onDisconnect }: Props) => (
  <Box mb={4}>
    <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
      sx={{ mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.08em' }}>
      Microsoft Account
    </Typography>
    <Divider sx={{ mb: 2.5 }} />
    <Grid container spacing={2.5}>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Card sx={{
          borderRadius: '12px', border: '1.5px solid',
          borderColor: msStatus.needsReconnect ? 'warning.main' : '#E5E7EB',
          boxShadow: 'none', height: '100%', display: 'flex', flexDirection: 'column',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)', borderColor: 'primary.main' },
        }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #0078D4, #005A9E)', p: 2.5,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            minHeight: 80, borderRadius: '10px 10px 0 0',
          }}>
            <MicrosoftIcon sx={{ fontSize: 36, color: 'white' }} />
          </Box>
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
              <Typography fontWeight={600} fontSize="0.9375rem" color="text.primary">Microsoft Account</Typography>
              {msStatus.loading ? (
                <CircularProgress size={16} />
              ) : msStatus.isConnected ? (
                <Chip icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label="Connected" size="small"
                  sx={{ fontSize: '0.6875rem', height: 22, bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600, '& .MuiChip-icon': { color: '#059669' } }} />
              ) : msStatus.needsReconnect ? (
                <Chip icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />} label="Reconnect" size="small"
                  sx={{ fontSize: '0.6875rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600, '& .MuiChip-icon': { color: '#D97706' } }} />
              ) : (
                <Chip icon={<RadioButtonUncheckedIcon sx={{ fontSize: '14px !important' }} />} label="Not connected" size="small"
                  sx={{ fontSize: '0.6875rem', height: 22, bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 500 }} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, lineHeight: 1.6, mb: 1.5, fontSize: '0.8125rem' }}>
              {msStatus.m365NotConfigured
                ? "Your organisation admin hasn't connected Microsoft 365 yet — contact them to enable this."
                : msStatus.adminDisabled
                  ? 'Microsoft account linking has been disabled by your organisation admin.'
                  : msStatus.needsReconnect
                    ? 'Your Microsoft session has expired. Please reconnect to continue syncing Teams recordings and to link OneDrive folders to a client.'
                    : msStatus.isConnected
                      ? `Connected${msStatus.email ? ` as ${msStatus.email}` : ''}. Teams recordings and transcripts will sync automatically${isFileAccessMissing(msStatus) ? '' : ', and you can link OneDrive folders to a client'}.`
                      : 'Connect your personal Microsoft account to sync Teams meeting recordings and transcripts, and to link OneDrive folders to a client.'}
            </Typography>
            <Box display="flex" gap={0.75} flexWrap="wrap" mb={1.5}>
              {['MEETING', 'CALENDAR', 'FILES'].map((cap) => {
                // A capability the connection was never granted is shown greyed
                // rather than hidden: the user needs to know OneDrive linking
                // exists AND that their connection cannot do it yet. Hiding it
                // would make the feature look absent; showing it as active
                // would promise something that returns 403.
                const unavailable = cap === 'FILES' && isFileAccessMissing(msStatus);
                return (
                  <Chip key={cap} label={cap} size="small"
                    title={unavailable ? 'Reconnect your Microsoft account to enable OneDrive file access' : undefined}
                    sx={{
                      fontSize: '0.625rem', height: 18, fontWeight: 600, borderRadius: '4px',
                      bgcolor: unavailable ? '#F3F4F6' : '#EFF6FF',
                      color: unavailable ? '#9CA3AF' : '#1D4ED8',
                    }} />
                );
              })}
            </Box>
            {isFileAccessMissing(msStatus) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.75rem' }}>
                Reconnect to enable OneDrive file access — your connection predates it.
              </Typography>
            )}
            <Box display="flex" alignItems="center" justifyContent="space-between" mt="auto" gap={1}>
              {msStatus.isConnected ? (
                <>
                  <Button size="small" variant="outlined" color="error" onClick={onDisconnect} disabled={msDisconnecting} sx={{ fontSize: '0.8125rem' }}>
                    {msDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </Button>
                  {/* Re-running consent is the ONLY way to gain a scope, and a
                      connected user otherwise has no button that does it — they
                      would have to guess at disconnect-then-connect. */}
                  {isFileAccessMissing(msStatus) && (
                    <Button size="small" variant="contained" onClick={onConnect} endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.8125rem' }}>
                      Reconnect
                    </Button>
                  )}
                </>
              ) : msStatus.m365NotConfigured || msStatus.adminDisabled ? (
                <Button size="small" variant="contained" disabled fullWidth sx={{ fontSize: '0.8125rem' }}>Not available</Button>
              ) : (
                <Button size="small" variant="contained" fullWidth onClick={onConnect} endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.8125rem' }}>
                  {msStatus.needsReconnect ? 'Reconnect' : 'Connect'}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Box>
);

export default MicrosoftIntegrationSection;
